/* ── RequestStandard — CTA for corpus-gap responses ─────────────
   Appears after corpus-gap indicators when a standard is missing.
   Lets users request the standard be added to the corpus.
   ─────────────────────────────────────────────────────────────── */

import { useState } from 'react';
import { submitIssue } from '../api';
import { useToast } from '../contexts/ToastContext';

interface Props {
  /** Standard name extracted from the gap text (pre-fills the form) */
  standardName: string;
  /** Called when the banner/form should be dismissed */
  onDismiss?: () => void;
}

const NOTE_MAX = 100;

const PRIORITIES: { value: string; label: string; emoji: string }[] = [
  { value: 'nice_to_have', label: 'Nice to have', emoji: '○' },
  { value: 'need_soon',    label: 'Need it soon', emoji: '◑' },
  { value: 'urgent',       label: 'Urgent',       emoji: '●' },
];

/**
 * Map our priority labels to the existing IssueSubmission severity values
 * so the backend can process them without changes.
 */
function priorityToSeverity(priority: string): 'minor' | 'important' | 'critical' {
  if (priority === 'urgent') return 'critical';
  if (priority === 'need_soon') return 'important';
  return 'minor';
}

export default function RequestStandard({ standardName, onDismiss }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState(standardName);
  const [priority, setPriority] = useState('nice_to_have');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  const trimmedNote = note.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || submitted || !name.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      await submitIssue({
        category: 'standard_request',
        severity: priorityToSeverity(priority),
        description: `Standard request: ${name.trim()}${trimmedNote ? ` — ${trimmedNote}` : ''}`,
      });
      setSubmitted(true);
      addToast('success', 'Standard request submitted', 3000);
    } catch {
      setError('Failed to submit — please try again.');
      addToast('error', 'Failed to submit request', 3000);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="request-standard-banner request-standard-banner--success" role="status">
        <svg
          className="request-standard-check"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>Request submitted! We'll notify you when it's added.</span>
      </div>
    );
  }

  return (
    <div className="request-standard-banner" role="region" aria-label="Request a standard">
      {/* Collapsed CTA */}
      {!formOpen && (
        <div className="request-standard-cta">
          <div className="request-standard-cta-text">
            <svg
              className="request-standard-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            <span>
              We don't have <strong>{standardName}</strong> yet. Request it and we'll prioritize adding it.
            </span>
          </div>
          <div className="request-standard-cta-actions">
            <button
              className="request-standard-btn"
              onClick={() => setFormOpen(true)}
            >
              Request it
            </button>
            {onDismiss && (
              <button
                className="request-standard-dismiss"
                onClick={onDismiss}
                aria-label="Dismiss"
                type="button"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Expanded form */}
      {formOpen && (
        <form className="request-standard-form" onSubmit={handleSubmit} noValidate>
          <div className="request-standard-form-header">
            <span className="request-standard-form-title">Request a Standard</span>
            <button
              className="request-standard-dismiss"
              onClick={() => setFormOpen(false)}
              aria-label="Close form"
              type="button"
            >
              ✕
            </button>
          </div>

          {/* Standard name */}
          <div className="request-standard-field">
            <label className="request-standard-label" htmlFor="req-std-name">
              Standard name
            </label>
            <input
              id="req-std-name"
              className="request-standard-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AS/NZS 3000:2018"
              disabled={submitting}
            />
          </div>

          {/* Priority */}
          <div className="request-standard-field">
            <span className="request-standard-label">Priority</span>
            <div className="request-standard-priority" role="radiogroup" aria-label="Priority">
              {PRIORITIES.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  className={`request-standard-priority-option${priority === p.value ? ' request-standard-priority-option--selected' : ''}${p.value === 'urgent' ? ' request-standard-priority-option--urgent' : ''}`}
                  onClick={() => setPriority(p.value)}
                  disabled={submitting}
                  aria-checked={priority === p.value}
                >
                  <span className="request-standard-priority-dot" aria-hidden="true">
                    {p.emoji}
                  </span>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Optional note */}
          <div className="request-standard-field">
            <label className="request-standard-label" htmlFor="req-std-note">
              Note <span className="request-standard-optional">(optional)</span>
            </label>
            <input
              id="req-std-note"
              className="request-standard-input"
              type="text"
              value={note}
              onChange={(e) => {
                if (e.target.value.length <= NOTE_MAX) setNote(e.target.value);
              }}
              placeholder="Why do you need it?"
              maxLength={NOTE_MAX}
              disabled={submitting}
            />
            <span className="request-standard-char-count" aria-live="polite">
              {trimmedNote.length}/{NOTE_MAX}
            </span>
          </div>

          {error && (
            <div className="request-standard-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="request-standard-submit"
            disabled={submitting || !name.trim()}
            aria-disabled={submitting || !name.trim()}
          >
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </form>
      )}
    </div>
  );
}
